/**
 * Internal DNC — shared rule parser/classifier.
 *
 * Accepts raw user input (typed, pasted, or CSV-row) and classifies it into
 * one of the three storage buckets used by the backend (areaCodes /
 * exactNumbers / prefixes). Trailing-`*` is sugar for a prefix rule.
 */

export type RuleKind = 'areaCode' | 'exactNumber' | 'prefix';

export type ClassifyResult =
  | { kind: RuleKind; value: string; displayValue: string }
  | { kind: 'invalid'; reason: string };

const AREA_CODE = /^\d{3}$/;
const EXACT = /^\+\d{8,15}$/;
const WILDCARD = /^\+\d{1,15}\*$/;
const PREFIX_SHORT = /^\+\d{1,7}$/;

/**
 * Classify a single trimmed input into a DNC rule type.
 * Returns the canonical value to store (no trailing `*`) and a displayValue
 * suitable for showing in chips (trailing `*` for prefixes).
 */
export function classifyRule(rawInput: string): ClassifyResult {
  const v = rawInput.trim();
  if (!v) return { kind: 'invalid', reason: 'Empty value' };

  if (AREA_CODE.test(v)) {
    return { kind: 'areaCode', value: v, displayValue: v };
  }

  if (WILDCARD.test(v)) {
    const stripped = v.slice(0, -1);
    return { kind: 'prefix', value: stripped, displayValue: `${stripped}*` };
  }

  if (EXACT.test(v)) {
    return { kind: 'exactNumber', value: v, displayValue: v };
  }

  if (PREFIX_SHORT.test(v)) {
    return { kind: 'prefix', value: v, displayValue: `${v}*` };
  }

  if (!v.startsWith('+') && /^\d+$/.test(v)) {
    return { kind: 'invalid', reason: 'Numbers must start with + (E.164). Area codes are 3 digits only.' };
  }

  if (v.includes('*') && !WILDCARD.test(v)) {
    return { kind: 'invalid', reason: 'Wildcard `*` is only allowed at the end, after +<digits>.' };
  }

  return { kind: 'invalid', reason: 'Use a 3-digit area code, E.164 number (+14155551234), or prefix wildcard (+1404555*).' };
}

/**
 * Split a multi-line / comma-separated paste into individual candidate entries.
 */
export function splitEntries(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Display helper — show a stored prefix value with its wildcard sugar.
 */
export function formatPrefixForDisplay(prefix: string): string {
  return prefix.endsWith('*') ? prefix : `${prefix}*`;
}
